package com.anjuros.website.redesign.sections.contactforms

import androidx.compose.runtime.Composable
import com.anjuros.website.components.language.LocalText
import com.anjuros.website.redesign.components.RdSection
import com.anjuros.website.redesign.components.RdSectionHead
import com.anjuros.website.redesign.styles.FormsFieldTypesStyle
import com.anjuros.website.redesign.styles.FormsMiniAreaStyle
import com.anjuros.website.redesign.styles.FormsMiniBoxStyle
import com.anjuros.website.redesign.styles.FormsMiniControlStyle
import com.anjuros.website.redesign.styles.FormsMiniDotStyle
import com.anjuros.website.redesign.styles.FormsMiniSelectStyle
import com.anjuros.website.redesign.util.RdText
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.attrsModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.style.toModifier
import org.jetbrains.compose.web.dom.B
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text

/** How each field type looks inside its sample: plain, a tall area, a dropdown arrow, or a tick/dot in front. */
private enum class Sample { PLAIN, AREA, SELECT, DATE, CHECK, RADIO }

private class FieldType(val name: RdText, val sample: RdText, val look: Sample)

private val FieldTypes = listOf(
    FieldType(RdText.FIELD_TYPE_TEXT, RdText.FIELD_TYPE_TEXT_SAMPLE, Sample.PLAIN),
    FieldType(RdText.FIELD_TYPE_EMAIL, RdText.FIELD_TYPE_EMAIL_SAMPLE, Sample.PLAIN),
    FieldType(RdText.FIELD_TYPE_PHONE, RdText.FIELD_TYPE_PHONE_SAMPLE, Sample.PLAIN),
    FieldType(RdText.FIELD_TYPE_AREA, RdText.FIELD_TYPE_AREA_SAMPLE, Sample.AREA),
    FieldType(RdText.FIELD_TYPE_SELECT, RdText.FIELD_TYPE_SELECT_SAMPLE, Sample.SELECT),
    FieldType(RdText.FIELD_TYPE_DATE, RdText.FIELD_TYPE_DATE_SAMPLE, Sample.DATE),
    FieldType(RdText.FIELD_TYPE_CHECK, RdText.FIELD_TYPE_CHECK_SAMPLE, Sample.CHECK),
    FieldType(RdText.FIELD_TYPE_RADIO, RdText.FIELD_TYPE_RADIO_SAMPLE, Sample.RADIO),
)

/** The field types a form can use, each with a small drawing of the control. */
@Composable
fun ContactFormsFieldTypesSection() {
    val label = LocalText(RdText.FORMS_FIELDS_LABEL)
    RdSection(id = "fields", labelledBy = "fields-h") {
        RdSectionHead(RdText.FORMS_FIELDS_EYEBROW, "fields-h", RdText.FORMS_FIELDS_TITLE, RdText.FORMS_FIELDS_LEDE)
        Div(FormsFieldTypesStyle.toModifier().attrsModifier { attr("aria-label", label) }.toAttrs()) {
            FieldTypes.forEach { type ->
                Div {
                    B { Text(LocalText(type.name)) }
                    MiniControl(type)
                }
            }
        }
    }
}

@Composable
private fun MiniControl(type: FieldType) {
    val shape = when (type.look) {
        Sample.AREA -> FormsMiniAreaStyle.toModifier()
        Sample.SELECT, Sample.DATE -> FormsMiniSelectStyle.toModifier()
        else -> Modifier
    }
    Div(FormsMiniControlStyle.toModifier().then(shape).attrsModifier { attr("aria-hidden", "true") }.toAttrs()) {
        when (type.look) {
            Sample.CHECK -> Span(FormsMiniBoxStyle.toModifier().toAttrs())
            Sample.RADIO -> Span(FormsMiniDotStyle.toModifier().toAttrs())
            else -> Unit
        }
        Text(LocalText(type.sample))
        when (type.look) {
            Sample.SELECT -> Span { Text("▾") }
            Sample.DATE -> Span { Text("▦") }
            else -> Unit
        }
    }
}
